/**
 * Period-over-period delta chips for the dashboard tiles.
 *
 * The rules exist because the naive version was noise: after a relearn every
 * tile screamed "▲19052%" against a near-empty prior week, and coverage
 * "climbed 65.9 pts" from a window that had no coverage figure at all.
 *
 * - No comparable prior (null, zero, or under 2% of the current value) reads
 *   `new`, and only when there is something now; otherwise no chip.
 * - Under 0.05% either way is "unchanged" (—).
 * - Percent change is capped at >999% so a real tenfold move still fits.
 * - Coverage moves in percentage points, not percent-of-percent; `new` only
 *   when the prior window had no coverage figure (a prior of 0% rising to 40%
 *   is a true +40 pts, not a division by nothing).
 * - Tone says which direction is good: revenue up is good, cost up is bad,
 *   requests up is just a fact.
 */
export type Tone = 'up-good' | 'up-bad' | 'neutral';

export interface Chip {
  text: string;
  title: string;
  cls: 'good' | 'bad' | 'muted';
}

const NEW: Chip = { text: 'new', title: 'no comparable activity in the previous period', cls: 'muted' };
const SAME: Chip = { text: '—', title: 'unchanged from the previous period', cls: 'muted' };

export function chip(
  cur: number | null | undefined,
  prev: number | null | undefined,
  tone: Tone,
  fmt: (n: number) => string = (n) => String(n),
): Chip | null {
  if (cur == null) return null;
  if (prev == null || prev === 0 || Math.abs(prev) < 0.02 * Math.abs(cur)) {
    return cur > 0 ? NEW : null;
  }
  // Against |prev| so a loss turning into a gain reads as up, not as -150%.
  const pct = (100 * (cur - prev)) / Math.abs(prev);
  if (Math.abs(pct) < 0.05) return SAME;
  const up = pct > 0;
  const mag = Math.abs(pct) > 999 ? '>999%' : Math.abs(pct).toFixed(1) + '%';
  const cls: Chip['cls'] = tone === 'neutral' ? 'muted' : up === (tone === 'up-good') ? 'good' : 'bad';
  return {
    text: `${up ? '▲' : '▼'} ${mag}`,
    title: `${up ? 'up' : 'down'} ${mag} from ${fmt(prev)} in the previous period`,
    cls,
  };
}

/** Coverage: a difference in percentage points. */
export function pointsChip(cur: number | null | undefined, prev: number | null | undefined): Chip | null {
  if (cur == null) return null;
  if (prev == null) return { ...NEW, title: 'no coverage figure for the previous period' };
  const d = cur - prev;
  if (Math.abs(d) < 0.05) return SAME;
  const up = d > 0;
  return {
    text: `${up ? '▲' : '▼'} ${Math.abs(d).toFixed(1)} pts`,
    title: `${up ? 'up' : 'down'} ${Math.abs(d).toFixed(1)} points from ${prev.toFixed(1)}% in the previous period`,
    cls: up ? 'good' : 'bad',
  };
}
